const pageFiltersYieldToMain = () => {
  const yieldFn = globalThis.scheduler?.yield
  if (typeof yieldFn === 'function') return yieldFn.call(globalThis.scheduler)
  return new Promise(resolve => {
    setTimeout(resolve, 0)
  })
}

// All Systems 弹窗内部的分类切换和型号定位
class PageFiltersSearchAllModal extends HTMLElement {
  connectedCallback() {
    this.listenerAbort?.abort()
    this.listenerAbort = new AbortController()
    const { signal } = this.listenerAbort

    this.modal = this.closest('modal-container')
    this.scrollTabs = this.querySelector('scroll-tabs')

    this.addEventListener('click', this.onClick, { signal })
    this.scrollTabs?.addEventListener('scroll-tabs:select', this.onScrollTabsSelect, { signal })
    this.modal?.addEventListener('modal:show', this.onModalShow, { signal })
    this.modal?.addEventListener('modal:opened', this.onModalOpened, { signal })
    this.modal?.addEventListener('modal:hide', this.onModalHide, { signal })
  }

  disconnectedCallback() {
    this.listenerAbort?.abort()
  }

  get controller() {
    return document.getElementById(this.dataset.controllerId)
  }

  get tabs() {
    return [...this.querySelectorAll('[data-all-systems-tab]')]
  }

  get panels() {
    return [...this.querySelectorAll('[data-all-systems-panel]')]
  }

  onModalShow = event => {
    const tabId = this.getOpenerTabId(event)
    const selected = this.getSelectedModel(tabId)
    const nextTabId = selected.tabId || tabId
    const tab = this.tabs.find(item => item.dataset.allSystemsTab === nextTabId) || this.tabs[0]
    if (!tab) return
    this.selectTab(tab)
    this.syncSelectedItem(tab.dataset.allSystemsTab, selected)
  }

  onModalOpened = () => {
    const tab = this.tabs.find(item => item.classList.contains('selected'))
    tab?.focus()
    this.scrollTabs?.scrollTabToStart?.(tab)
    this.scrollSelectedItemIntoView()
  }

  onModalHide = () => {
    this.modal.openedBy?.querySelector('button')?.focus()
  }

  onClick = event => {
    const item = event.target.closest('[data-all-systems-item]')
    if (!item || !this.contains(item)) return

    const selected = this.controller?.selectModel(item.dataset.searchGroup, item.dataset.searchShortName, item)
    if (selected) this.modal?.hide()
  }

  onScrollTabsSelect = event => {
    const { tab } = event.detail
    this.showTabPanel(tab)
    this.syncSelectedItem(tab.dataset.allSystemsTab)
  }

  selectTab(tab) {
    if (!tab) return
    this.scrollTabs?.selectTab?.(tab, { silent: true })
    this.showTabPanel(tab)
  }

  showTabPanel(tab) {
    const tabId = tab.dataset.allSystemsTab

    this.panels.forEach(panel => {
      const selected = panel.dataset.allSystemsPanel === tabId
      panel.classList.toggle('selected', selected)
      panel.hidden = !selected
    })
  }

  getOpenerTabId(event) {
    const opener = event?.detail?.opener || this.modal?.openedBy
    if (!(opener instanceof HTMLElement)) return ''
    return opener.dataset.modalTab || opener.closest('[data-modal-tab]')?.dataset.modalTab || ''
  }

  getSelectedModel(tabId) {
    const { controller } = this
    if (!controller) return { tabId, shortName: '', groupName: '' }

    const tabs = controller.tabs || []
    const outerTab =
      tabs.find(tab => tab.dataset.tabId === tabId) || tabs.find(tab => tab.classList.contains('selected'))
    const outerPanel = outerTab && (controller.panels || []).find(panel => panel.id === outerTab.dataset.tabId)
    const sliderItem = outerPanel?.querySelector('.page-filters-search-list__item[data-controller-id].selected')
    const shortName = sliderItem?.dataset.searchShortName || ''
    const groupName = sliderItem?.dataset.searchGroup || ''

    const matched = [...this.querySelectorAll('[data-all-systems-item]')].find(item => {
      if (shortName && item.dataset.searchShortName !== shortName) return false
      if (groupName && item.dataset.searchGroup !== groupName) return false
      return Boolean(shortName)
    })

    return {
      tabId: matched?.closest('[data-all-systems-panel]')?.dataset.allSystemsPanel || tabId,
      shortName,
      groupName
    }
  }

  syncSelectedItem(tabId, selectedModel = this.getSelectedModel(tabId)) {
    const selectedName = selectedModel?.shortName
    const selectedGroup = selectedModel?.groupName

    this.querySelectorAll('[data-all-systems-item]').forEach(item => {
      const selected =
        item.closest('[data-all-systems-panel]')?.dataset.allSystemsPanel === tabId &&
        Boolean(selectedName) &&
        item.dataset.searchShortName === selectedName &&
        (!selectedGroup || item.dataset.searchGroup === selectedGroup)
      item.classList.toggle('selected', selected)
      item.setAttribute('aria-pressed', selected ? 'true' : 'false')
    })
  }

  scrollSelectedItemIntoView() {
    this.querySelector('[data-all-systems-panel]:not([hidden]) [data-all-systems-item].selected')?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest'
    })
  }
}

if (!customElements.get('page-filters-search-all-modal')) {
  customElements.define('page-filters-search-all-modal', PageFiltersSearchAllModal)
}

// 搜索、型号选择和产品内容切换的统一控制器
class PageFiltersSearchController extends HTMLElement {
  connectedCallback() {
    this.search = this.querySelector('filters-model-search')
    this.pageTabs = this.querySelector('scroll-tabs.page-filters-search-tabs')
    this.contentCache = this.contentCache || new Map()
    this.querySelectorAll('.page-filters-search-list__item-content').forEach(content => {
      if (content.id) this.contentCache.set(content.id, content)
    })

    if (!this.eventsBound) {
      this.eventsBound = true
      this.addEventListener('click', this.onTabClick)
      this.addEventListener('keydown', this.onTabKeydown)
    }

    if (!this.tabs.length || !this.panels.length) {
      queueMicrotask(() => this.initSelection())
      return
    }

    this.initSelection()
  }

  initSelection() {
    this.search = this.search || this.querySelector('filters-model-search')
    this.pageTabs = this.pageTabs || this.querySelector('scroll-tabs.page-filters-search-tabs')

    if (this.search && !this.searchBound) {
      this.searchBound = true
      this.search.addEventListener('filters-model-search:select', this.onSearchSelect)
    }
    if (this.pageTabs && !this.pageTabsBound) {
      this.pageTabsBound = true
      this.pageTabs.addEventListener('scroll-tabs:select', this.onPageTabsSelect)
    }

    if (this.selectionInitialized || !this.tabs.length || !this.panels.length) return
    this.selectionInitialized = true

    const initialTab = this.tabs.find(tab => tab.classList.contains('selected')) || this.tabs[0]
    this.selectTab(initialTab)

    const activeContent = this.querySelector('.page-filters-search-list__item-content.selected')
    const initialContentTab =
      [...(activeContent?.querySelectorAll('.page-filters-search-list__item-content-tab[aria-controls]') || [])].find(
        tab => tab.classList.contains('selected')
      ) || activeContent?.querySelector('.page-filters-search-list__item-content-tab[aria-controls]')
    if (initialContentTab) this.selectContentTab(initialContentTab)
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onTabClick)
    this.removeEventListener('keydown', this.onTabKeydown)
    this.search?.removeEventListener('filters-model-search:select', this.onSearchSelect)
    this.pageTabs?.removeEventListener('scroll-tabs:select', this.onPageTabsSelect)
    this.contentCache?.clear()
    this.pendingContentId = ''
    this.pendingPanel = null
    this.mountQueued = false
    this.eventsBound = false
    this.searchBound = false
    this.pageTabsBound = false
    this.selectionInitialized = false
  }

  get tabs() {
    return [...this.querySelectorAll('.page-filters-search-tabs__item[data-tab-id]')]
  }

  get panels() {
    return [...this.querySelectorAll('.page-filters-search-list__panel')]
  }

  onTabClick = event => {
    const contentTab = event.target.closest('.page-filters-search-list__item-content-tab[aria-controls]')
    if (contentTab && this.contains(contentTab)) {
      this.selectContentTab(contentTab)
      return
    }

    const item = event.target.closest('.page-filters-search-list__item[data-controller-id]')
    if (!item || !this.contains(item)) return

    this.selectItem(item)
  }

  onSearchSelect = event => {
    this.selectModel(event.detail.groupName, event.detail.shortName)
  }

  onPageTabsSelect = event => {
    const { tab } = event.detail
    this.activateTabPanel(tab)
  }

  selectModel(groupName, shortName, sourceItem) {
    const tab = this.tabs.find(item => item.dataset.searchGroup === groupName)
    if (!tab) return false

    this.selectTab(tab)
    const targetPanel = this.panels.find(panel => panel.id === tab.dataset.tabId)
    if (!targetPanel) return false

    const item = this.ensureSlotModelItem(targetPanel, groupName, shortName, sourceItem)
    if (!item) return false

    this.selectItem(item)
    this.moveSliderToItem(item)
    const tabsEl = this.querySelector('.page-filters-search-tabs')
    if (tabsEl) {
      const top = Math.max(0, window.scrollY + tabsEl.getBoundingClientRect().top)
      window.scrollTo({ top, behavior: 'smooth' })
    }
    return true
  }

  findSliderItem(panel, shortName) {
    return [...panel.querySelectorAll('.page-filters-search-list__item[data-controller-id]')].find(
      card => card.dataset.searchShortName === shortName
    )
  }

  getSlotModelItem(panel) {
    return (
      panel.querySelector('.page-filters-search-list__item--slot') ||
      [...panel.querySelectorAll('.page-filters-search-list__item[data-controller-id]')].at(13) ||
      null
    )
  }

  findModalSourceItem(groupName, shortName) {
    const modal = document.querySelector(`page-filters-search-all-modal[data-controller-id="${CSS.escape(this.id)}"]`)
    return [...(modal?.querySelectorAll('[data-all-systems-item]') || [])].find(
      item => item.dataset.searchGroup === groupName && item.dataset.searchShortName === shortName
    )
  }

  fillSlotModelItem(item, source, contentId) {
    item.dataset.controllerId = contentId
    item.dataset.searchGroup = source.dataset.searchGroup
    item.dataset.searchShortName = source.dataset.searchShortName

    const title = item.querySelector('.page-filters-search-list__item-title')
    const imageWrap = item.querySelector('.page-filters-search-list__item-image')
    const sourceTitle = source.querySelector('.page-filters-search-all-modal__item-title')
    const sourceImage = source.querySelector('.page-filters-search-all-modal__item-image')

    if (title) title.textContent = sourceTitle?.textContent?.trim() || source.dataset.searchShortName
    if (imageWrap && sourceImage) imageWrap.replaceChildren(...[...sourceImage.cloneNode(true).children])
  }

  findItemContentId(panel, shortName) {
    const live = [...panel.querySelectorAll('.page-filters-search-list__item-content')].find(
      item => item.dataset.searchShortName === shortName
    )
    if (live) return live.dataset.itemContentId || live.id || ''

    const template = [...panel.querySelectorAll('template[data-item-content-id]')].find(
      item => item.dataset.searchShortName === shortName
    )
    return template?.dataset.itemContentId || ''
  }

  getItemContentTemplate(targetId) {
    return this.querySelector(`template[data-item-content-id="${CSS.escape(targetId)}"]`)
  }

  ensureSlotModelItem(panel, groupName, shortName, sourceItem) {
    const existing = this.findSliderItem(panel, shortName)
    if (existing) return existing

    const slotItem = this.getSlotModelItem(panel)
    const source = sourceItem || this.findModalSourceItem(groupName, shortName)
    const contentId = this.findItemContentId(panel, shortName)
    if (!slotItem || !source || !contentId) return null

    this.fillSlotModelItem(slotItem, source, contentId)
    return slotItem
  }

  moveSliderToItem(item) {
    const sliderElement = item.closest('.page-filters-search-list__splide')
    if (!sliderElement) return

    const slides = [...sliderElement.querySelectorAll('.page-filters-search-list__item[data-controller-id]')]
    const targetIndex = slides.indexOf(item)
    if (targetIndex < 0) return

    const move = slider => {
      if (!slider) return
      slider.refresh()
      slider.go(targetIndex)
    }

    if (sliderElement.slider) {
      window.requestAnimationFrame(() => move(sliderElement.slider))
      return
    }

    sliderElement.addEventListener('splide:loaded', event => move(event.detail?.slider), { once: true })
  }

  onTabKeydown = event => {
    const contentTab = event.target.closest('.page-filters-search-list__item-content-tab[aria-controls]')
    if (contentTab && this.contains(contentTab)) {
      this.handleContentTabKeydown(event, contentTab)
      return
    }

    const item = event.target.closest('.page-filters-search-list__item[data-controller-id]')
    if (!item || !this.contains(item) || !['Enter', ' '].includes(event.key)) return

    event.preventDefault()
    this.selectItem(item)
  }

  handleContentTabKeydown(event, tab) {
    const tablist = tab.closest('.page-filters-search-list__item-content-tabs')
    const tabs = [...tablist.querySelectorAll('.page-filters-search-list__item-content-tab[aria-controls]')]
    const currentIndex = tabs.indexOf(tab)
    let targetIndex = currentIndex

    if (event.key === 'ArrowRight') targetIndex = (currentIndex + 1) % tabs.length
    if (event.key === 'ArrowLeft') targetIndex = (currentIndex - 1 + tabs.length) % tabs.length
    if (event.key === 'Home') targetIndex = 0
    if (event.key === 'End') targetIndex = tabs.length - 1
    if (targetIndex === currentIndex) return

    event.preventDefault()
    const targetTab = tabs[targetIndex]
    targetTab.focus()
    this.selectContentTab(targetTab)
  }

  selectTab(tab) {
    this.pageTabs?.selectTab?.(tab, { silent: true })
    this.activateTabPanel(tab)
  }

  activateTabPanel(tab) {
    const targetId = tab.dataset.tabId
    const targetPanel = this.panels.find(panel => panel.id === targetId)
    if (!targetPanel) return

    this.panels.forEach(panel => {
      const selected = panel === targetPanel
      panel.classList.toggle('selected', selected)
      panel.setAttribute('aria-hidden', selected ? 'false' : 'true')
      panel.hidden = !selected
    })

    const selectedItem = targetPanel.querySelector('.page-filters-search-list__item[data-controller-id].selected')
    if (selectedItem) {
      this.moveSliderToItem(selectedItem)
      this.showItemContent(targetPanel, selectedItem.dataset.controllerId)
      return
    }

    // 面板显示后刷新轮播尺寸，避免隐藏状态初始化导致宽度异常
    window.requestAnimationFrame(() => {
      targetPanel.querySelector('splide-slider')?.slider?.refresh()
    })
  }

  selectItem(item) {
    const panel = item.closest('.page-filters-search-list__panel')
    const targetId = item.dataset.controllerId
    if (!panel || !targetId) return

    panel.querySelectorAll('.page-filters-search-list__item[data-controller-id]').forEach(card => {
      const selected = card === item
      card.classList.toggle('selected', selected)
      card.setAttribute('aria-pressed', selected ? 'true' : 'false')
    })

    this.showItemContent(panel, targetId)
  }

  showItemContent(panel, targetId) {
    if (!panel || !targetId) return

    this.pendingContentId = targetId
    this.pendingPanel = panel
    const cached = this.contentCache.get(targetId)
    if (cached) {
      this.setActiveContent(cached)
      return
    }

    if (this.mountQueued) return
    this.mountQueued = true
    pageFiltersYieldToMain().then(() => {
      this.mountQueued = false
      if (!this.pendingContentId || !this.pendingPanel || !this.isConnected) return
      this.mountItemContent(this.pendingPanel, this.pendingContentId)
    })
  }

  setActiveContent(target) {
    const host = target.closest('[data-item-content-host]')
    if (!host) return

    host.querySelectorAll('.page-filters-search-list__item-content').forEach(content => {
      const selected = content === target
      content.classList.toggle('selected', selected)
      content.hidden = !selected
      content.setAttribute('aria-hidden', selected ? 'false' : 'true')
    })
  }

  mountItemContent(panel, targetId) {
    const cached = this.contentCache.get(targetId)
    if (cached) {
      this.setActiveContent(cached)
      return
    }

    const host = panel?.querySelector('[data-item-content-host]')
    const source = this.getItemContentTemplate(targetId)?.content.querySelector(
      '.page-filters-search-list__item-content'
    )
    if (!host || !source) return

    const live = source.cloneNode(true)
    if (this.pendingContentId !== targetId) return

    this.contentCache.set(targetId, live)
    host.append(live)
    this.setActiveContent(live)
  }

  selectContentTab(tab) {
    const content = tab.closest('.page-filters-search-list__item-content')
    const targetPanel = content?.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`)
    if (!content || !targetPanel) return

    content.querySelectorAll('.page-filters-search-list__item-content-tab[aria-controls]').forEach(item => {
      const selected = item === tab
      item.classList.toggle('selected', selected)
      item.setAttribute('aria-selected', selected ? 'true' : 'false')
      item.tabIndex = selected ? 0 : -1
    })

    content.querySelectorAll('.page-filters-search-list__item-content-tabs-panel').forEach(panel => {
      const selected = panel === targetPanel
      panel.classList.toggle('selected', selected)
      panel.setAttribute('aria-hidden', selected ? 'false' : 'true')
      panel.hidden = !selected
    })
  }
}

if (!customElements.get('page-filters-search-controller')) {
  customElements.define('page-filters-search-controller', PageFiltersSearchController)
}
